import { BitkitBadge, BitkitExpandableCard, BitkitTooltip, IconCalendar } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';

import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import { scheduleBuildPath } from '@/utils/monolithApiRouteService';

import ScheduledBuildItem from './ScheduledBuildItem';
import { ScheduledBuild, ScheduledBuildResponse } from './scheduledBuilds.types';

const Badges = ({ scheduledCount, invalidCount }: { scheduledCount: number; invalidCount: number }) => (
  <>
    <BitkitBadge marginInline="4" colorVariant="neutral" variant="subtle">
      {scheduledCount}
    </BitkitBadge>
    {!!invalidCount && (
      <BitkitTooltip text="Invalid schedules will not start builds until they are fixed. If you no longer need them, simply delete them.">
        <BitkitBadge variant="subtle" colorVariant="red">
          {`${invalidCount} INVALID`}
        </BitkitBadge>
      </BitkitTooltip>
    )}
  </>
);

type ScheduledBuildsProps = {
  app: AppForAppPage;
  onUpdateScheduledBuild: (scheduledBuild: ScheduledBuild) => void;
};

const ScheduledBuilds = (props: ScheduledBuildsProps) => {
  const { app, onUpdateScheduledBuild } = props;
  const { data } = useMonolithApiQuery<ScheduledBuildResponse, null>(
    ['scheduledBuilds', app.slug],
    scheduleBuildPath(app.slug, {
      params: {
        build_trigger_token: app.buildTriggerToken,
      },
    }),
  );

  if (data?.schedules && data.schedules.length > 0) {
    const invalidCount = data.schedules.reduce((count, schedule) => {
      if (schedule.lastErrorMessage) {
        return count + 1;
      }
      return count;
    }, 0);
    return (
      <BitkitExpandableCard
        elevation={false}
        icon={IconCalendar}
        title={
          // v1 kept the badges next to the label, not in the right-hand group `suffix` renders into.
          <Box display="flex" alignItems="center" gap="4">
            <Text as="span" textStyle="body/lg/semibold">
              Scheduled builds
            </Text>
            <Badges scheduledCount={data.schedules.length} invalidCount={invalidCount} />
          </Box>
        }
      >
        {data.schedules.map((itemData, index) => (
          <ScheduledBuildItem
            key={itemData.id}
            data-testid={`scheduled-build-item-${index}`}
            app={app}
            scheduledBuildItemData={itemData}
            onUpdateScheduledBuild={onUpdateScheduledBuild}
            marginBlockEnd={index < data.schedules.length - 1 ? '16' : undefined}
            lastErrorMessage={itemData.lastErrorMessage}
          />
        ))}
      </BitkitExpandableCard>
    );
  }

  return null;
};

export default ScheduledBuilds;
